import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { ApiError, api } from '@/lib/api';
import { Alert, Button, Card, Field } from '@/components/ui';
import { PasswordInput } from '@/components/PasswordInput';
import { AuthShell } from '@/layouts/AuthShell';

/** Set a new password from the emailed link. */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password, confirmPassword });
      setDone(true);
      toast.success('Password changed. Sign in with your new password.');
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFieldErrors(caught.fieldErrors);
        setError(caught.fields.length ? null : caught.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  // A link with no token is a link that was truncated by an email client.
  if (!token) {
    return (
      <AuthShell title="That link is incomplete" description="The reset link is missing its token.">
        <Card className="p-7 sm:p-8">
          <Alert tone="warning" className="mb-6">
            Some email clients break long links across lines. Copy the whole link from the message,
            or request a new one.
          </Alert>
          <Link to="/forgot-password">
            <Button full>Request a new link</Button>
          </Link>
        </Card>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password changed" description="You can sign in with it now.">
        <Card className="p-6 sm:p-8">
          <span className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-success-soft text-success">
            <CheckCircle2 size={22} />
          </span>
          <p className="mb-6 leading-relaxed text-foreground-secondary">
            Any other devices that were signed in have been signed out.
          </p>
          <Button full onClick={() => navigate('/login')}>
            Go to sign in
          </Button>
        </Card>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a new password" description="Make it one you have not used elsewhere.">
      <Card className="p-6 sm:p-8">
        <form onSubmit={handleSubmit} className="space-y-5" noValidate>
          {error && <Alert tone="danger">{error}</Alert>}

          <Field
            label="New password"
            htmlFor="password"
            error={fieldErrors['password']}
            hint="At least 12 characters, with upper case, lower case and a number."
            required
          >
            <PasswordInput
              id="password"
              autoComplete="new-password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              invalid={Boolean(fieldErrors['password'])}
            />
          </Field>

          <Field
            label="Confirm new password"
            htmlFor="confirmPassword"
            error={fieldErrors['confirmPassword']}
            required
          >
            <PasswordInput
              id="confirmPassword"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              invalid={Boolean(fieldErrors['confirmPassword'])}
            />
          </Field>

          <Button type="submit" full size="lg" loading={loading}>
            <ShieldCheck size={16} /> Change password
          </Button>
        </form>
      </Card>
    </AuthShell>
  );
}
